/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { JSResourceForInteraction } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import PlaybookTemplateCreateQuery$parameters from '@jira/playbooks__edit-screen/src/__generated__/PlaybookTemplateCreateQuery$parameters.ts';
import type {
	TemplateCreateQuery,
	TemplateCreateExtraProps as ExtraProps,
} from '@jira/playbooks__edit-screen/src/FormValues.types.tsx';
import type { PlaybookTemplateCreateViewEntrypointProps } from './PlaybookTemplateCreateView.entrypoint.types.tsx';

type PlaybookTemplateCreateViewEntryPointComponent = EntryPointComponent<
	TemplateCreateQuery,
	Record<string, never>,
	PlaybookTemplateCreateViewEntrypointProps,
	ExtraProps
>;

const playbookTemplateCreateViewEntryPoint: EntryPoint<
	PlaybookTemplateCreateViewEntryPointComponent,
	EntryPointRouteParams
> = createEntryPoint({
	root: JSResourceForInteraction<PlaybookTemplateCreateViewEntryPointComponent>(
		/* webpackChunkName: "async-spa-apps-playbooks-template-create" */ './index.tsx',
	),
	getPreloadProps: ({ context, tenantContext }: EntryPointRouteParams) => {
		const { projectKey, templateId = '' } = context.match.params;
		if (!projectKey) {
			throw new Error('projectKey is required');
		}

		const { cloudId, activationId } = tenantContext;
		const playbookAri = toPlaybookAri({
			siteId: cloudId,
			activationId,
			playbookId: '',
		});

		return {
			queries: {
				PlaybookTemplateCreateQuery: {
					parameters: PlaybookTemplateCreateQuery$parameters,
					variables: {
						cloudId,
						projectKey,
						templateId: templateId ?? '',
					},
				},
			},
			extraProps: {
				playbookAri,
				isNew: true,
				variant: 'new' as const,
				templateId: templateId ?? '',
			},
		};
	},
});

export default playbookTemplateCreateViewEntryPoint;

function toPlaybookAri({
	siteId,
	activationId,
	playbookId,
}: {
	siteId: string;
	activationId: string;
	playbookId: string;
}) {
	return `ari:cloud:jira:${siteId}:playbook/activation/${activationId}/${playbookId}`;
}
